/**
 * PlayLibraryPanel - Collapsible sidebar showing all plays from the playbook
 * Supports search, filtering, and virtualized rendering for performance
 * Follows SlidePanel pattern for consistency
 */

import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Search, Filter } from 'lucide-react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuCheckboxItem,
	DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import { usePaginatedQuery } from '@/hooks/UsePaginatedQuery'
import { fetchPlaybookPlaysPage, playKeys } from '@/api/queries/PlayQueries'
import { PlayLibraryCard } from './PlayLibraryCard'
import './document-editor.css'

const ANIMATION_DURATION = 800 // ms - match panel transition

interface PlayLibraryPanelProps {
	playbookId: string
	showPanel: boolean
}

export function PlayLibraryPanel({ playbookId, showPanel }: PlayLibraryPanelProps) {
	const [searchQuery, setSearchQuery] = useState('')
	const [filterPlayType, setFilterPlayType] = useState<'run' | 'pass' | null>(null)
	const [_draggedPlayId, setDraggedPlayId] = useState<string | null>(null)

	// Track whether content should be rendered (delayed unmount on hide)
	const [shouldRenderContent, setShouldRenderContent] = useState(showPanel)
	const parentRef = useRef<HTMLDivElement>(null)

	// Handle delayed unmount: render immediately on show, delay unmount on hide
	useEffect(() => {
		if (showPanel) {
			setShouldRenderContent(true)
		} else {
			const timer = setTimeout(() => {
				setShouldRenderContent(false)
			}, ANIMATION_DURATION)
			return () => clearTimeout(timer)
		}
	}, [showPanel])

	// Fetch plays using paginated query
	const {
		items: plays,
		isLoading,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = usePaginatedQuery({
		queryKey: playKeys.list(playbookId),
		fetchPage: async (cursor) => {
			const result = await fetchPlaybookPlaysPage(playbookId, 50, cursor)
			return {
				items: result.items ?? [],
				nextCursor: result.nextCursor ?? undefined,
				hasMore: result.nextCursor != null,
			}
		},
		getItemId: (play) => play.id,
		enabled: shouldRenderContent,
	})

	// Filter plays based on search and filters
	const filteredPlays = plays.filter((play) => {
		// Search filter
		if (searchQuery && !play.name.toLowerCase().includes(searchQuery.toLowerCase())) {
			return false
		}

		// Play type filter
		return !(filterPlayType && play.play_type !== filterPlayType);


	})

	// Setup virtualizer with dependency on parent ref
	const getScrollElement = useCallback(() => parentRef.current, [])
	const virtualizer = useVirtualizer({
		count: filteredPlays.length,
		getScrollElement,
		estimateSize: () => 80, // Approximate card height
		overscan: 5,
	})

	const handleDragStart = useCallback((playId: string) => {
		setDraggedPlayId(playId)
	}, [])

	// Load more plays when scrolling near end
	const virtualItems = virtualizer.getVirtualItems()
	useEffect(() => {
		const [lastItem] = [...virtualItems].reverse()
		if (!lastItem) return

		if (
			lastItem.index >= filteredPlays.length - 1 &&
			hasNextPage &&
			!isFetchingNextPage
		) {
			fetchNextPage()
		}
	}, [
		hasNextPage,
		fetchNextPage,
		filteredPlays.length,
		isFetchingNextPage,
		virtualItems,
	])

	return (
		<div
			className="play-library-panel"
			style={{
				width: showPanel ? '360px' : '0px',
			}}
		>
			{shouldRenderContent && (
				<div className="play-library-panel__content">
					{/* Search and filter header */}
					<div className="play-library-panel__header">
						<div className="play-library-panel__search">
							<Search size={16} className="play-library-panel__search-icon" />
							<Input
								placeholder="Search plays..."
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								className="play-library-panel__search-input"
							/>
						</div>

						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button
									variant="ghost"
									size="icon"
									className="play-library-panel__filter-button"
									data-active={filterPlayType !== null}
								>
									<Filter size={16} />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								<DropdownMenuCheckboxItem
									checked={filterPlayType === 'run'}
									onCheckedChange={(checked) =>
										setFilterPlayType(checked ? 'run' : null)
									}
								>
									Run Plays
								</DropdownMenuCheckboxItem>
								<DropdownMenuCheckboxItem
									checked={filterPlayType === 'pass'}
									onCheckedChange={(checked) =>
										setFilterPlayType(checked ? 'pass' : null)
									}
								>
									Pass Plays
								</DropdownMenuCheckboxItem>
							</DropdownMenuContent>
						</DropdownMenu>
					</div>

					{/* Virtualized play list */}
					<div className="play-library-panel__scroll" ref={parentRef}>
						{isLoading && filteredPlays.length === 0 ? (
							<div className="play-library-panel__loading">Loading plays...</div>
						) : filteredPlays.length === 0 ? (
							<div className="play-library-panel__empty">
								{searchQuery || filterPlayType
									? 'No plays match your filters'
									: 'No plays in this playbook'}
							</div>
						) : (
							<div
								style={{
									height: `${virtualizer.getTotalSize()}px`,
									width: '100%',
									position: 'relative',
								}}
							>
								{virtualizer.getVirtualItems().map((virtualItem) => {
									const play = filteredPlays[virtualItem.index]
									if (!play) return null

									return (
										<div
											key={virtualItem.key}
											style={{
												position: 'absolute',
												top: 0,
												left: 0,
												width: '100%',
												transform: `translateY(${virtualItem.start}px)`,
											}}
											ref={virtualizer.measureElement}
											data-index={virtualItem.index}
										>
											<PlayLibraryCard
												play={play}
												onDragStart={() => handleDragStart(play.id)}
												index={virtualItem.index}
											/>
										</div>
									)
								})}
							</div>
						)}
					</div>

					{/* Loading indicator for pagination */}
					{isFetchingNextPage && (
						<div className="play-library-panel__loading-more">
							Loading more...
						</div>
					)}
				</div>
			)}
		</div>
	)
}
